import { workspaceHelp } from "./screenHelpContent";
import { useScreenHelp } from "./ScreenHelp";
import { appPrompt } from "./appDialogs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addressReady,
  countryOf,
  groupsForSites,
  frameworkLabel,
} from "./cqcFrameworks";
import CqcAddressForm from "./CqcAddressForm";
import { cqcApi } from "./cqcApi";
import { applicabilityIndex } from "./cqcRollup";
import {
  buildCqcRows,
  readiness,
  profileReady,
  serviceGuidance,
  scopeKey,
  blankProfile,
} from "./cqcProfileModel";
import CqcEvidenceDialog from "./CqcEvidenceDialog";
import CqcWorklist from "./CqcWorklist";
import CqcServiceProfile from "./CqcServiceProfile";
import CqcProgress from "./CqcProgress";
import { StatusBadge } from "./cqcColors";
import "./cqc-workspace.css";

export default function CqcWorkspace({
  initialSiteId,
  pack,
  readOnly = false,
  lockSite = false,
  onConfigureProfile,
}) {
  const root = useRef(null),
    [data, setData] = useState(pack || null),
    [error, setError] = useState(""),
    [mode, setMode] = useState("overview"),
    [siteId, setSiteId] = useState(initialSiteId || ""),
    [step, setStep] = useState(0),
    [dialog, setDialog] = useState(null),
    [busy, setBusy] = useState(false),
    [showOther, setShowOther] = useState(false),
    [onlyUnconfigured, setOnlyUnconfigured] = useState(false),
    [editingProfile, setEditingProfile] = useState(null),
    [editingAddress, setEditingAddress] = useState(false),
    [groupFilter, setGroupFilter] = useState("");
  async function load() {
    if (readOnly && pack) return;
    try {
      const [s, a, p, w, e] = await Promise.all([
        cqcApi.listSites(),
        cqcApi.listAttestations(),
        cqcApi.listApplicability(),
        cqcApi.listAssignments(),
        cqcApi.listEvidenceItems(),
      ]);
      setData({
        sites: s.sites || [],
        attestations: a.attestations || [],
        applicability: p.applicability || [],
        assignments: w.assignments || [],
        evidenceItems: e.evidenceItems || [],
      });
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  useEffect(() => {
    if (pack) setData(pack);
    else load();
  }, [pack]); // eslint-disable-line react-hooks/exhaustive-deps
  const allSites = useMemo(
    () => (data ? data.sites || [data.site] : []),
    [data],
  );
  const sites = useMemo(
    () =>
      allSites.filter(
        (s) =>
          (!lockSite || s._id === initialSiteId) &&
          (!siteId || s._id === siteId),
      ),
    [allSites, siteId, lockSite, initialSiteId],
  );
  const groups = useMemo(() => groupsForSites(sites), [sites]);
  const rows = useMemo(() => buildCqcRows({ ...data, sites }), [data, sites]);
  const apps = useMemo(() => applicabilityIndex(data?.applicability), [data]);
  const activeKeys = new Set(rows.map((r) => r.key)),
    selectedIds = new Set(sites.map((s) => s._id));
  const retained = [
    ...(data?.attestations || []).map((a) => ({
      ...a,
      retainedKind: "Source",
    })),
    ...(data?.evidenceItems || []).map((a) => ({
      ...a,
      retainedKind: "Proof",
    })),
  ].filter(
    (a) =>
      selectedIds.has(a.siteId) &&
      !activeKeys.has(`${a.siteId}|${a.groupId}|${a.itemId}|${a.elementId}`),
  );
  const stats = readiness(rows),
    setupRows = rows.filter(
      (r) =>
        r.scope.mode === "required" &&
        (!groupFilter || r.group.id === groupFilter) &&
        (!onlyUnconfigured || !r.source),
    );
  const stepIndex = Math.min(step, Math.max(0, setupRows.length - 1)),
    current = setupRows[stepIndex];
  useScreenHelp({ ...workspaceHelp(mode, sites.length === 1 ? sites[0]?.name : "Combined organisations", readOnly),
    ...(mode === "setup" && current ? { title: `Evidence setup — ${current.element.label || current.item.label}`, can: ["Read the organisation- and service-specific guidance for this item.", "Configure its source and save it before moving to the next item.", "Use the previous and next controls to work through the remaining items."] } : {})
  }, 30);
  async function mutate(action) {
    setBusy(true);
    setError("");
    try {
      await action();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  function configureProfile(site) {
    if (onConfigureProfile) onConfigureProfile();
    else setEditingProfile(site);
  }
  async function setScope(row, mode) {
    const reason = (await appPrompt(
      mode === "required"
        ? "Optional note for including this evidence item:"
        : "Explain this organisation’s responsibilities and why this item is supporting or not applicable:",
      mode === "required" ? "Included after review" : row.scope.reason || "",
    ));
    if (reason === null) return;
    if (mode !== "required" && reason.trim().length < 8) {
      setError("Enter a meaningful reason of at least 8 characters.");
      return;
    }
    const profile = blankProfile(row.site);
    profile.decisions = {
      ...profile.decisions,
      [scopeKey(row.group.id, row.item.id, row.element.id)]: {
        mode,
        reason: reason.trim(),
      },
    };
    await mutate(() =>
      cqcApi.saveServiceProfile(
        row.site._id,
        { ...profile, confirmed: true },
        row.site.profileRevision || 0,
      ),
    );
  }
  function detail(row) {
    const g = serviceGuidance(
        row.site,
        row.group.id,
        row.item.id,
        row.element.id,
      ),
      canEdit =
        !readOnly &&
        profileReady(row.site) &&
        row.scope.mode !== "not-applicable";
    const legacy = apps[`${row.site._id}|${row.group.id}|${row.item.id}`];
    return (
      <div className="cqc-columns">
        <aside className="cqc-card">
          <h3>What this is about</h3>
          <strong>{row.item.label}</strong>
          <p>{g.focus}</p>
          <p className="cqc-muted">{g.serviceLabel}</p>
          {g.context.map((c) => (
            <p key={c}>{c}</p>
          ))}
          {g.functionNotes.map((c) => (
            <p key={c}>{c}</p>
          ))}
          <h4>What proof is needed — {row.element.label}</h4>
          <p>{g.categoryHelp}</p>
          <ul>
            {g.proofExamples.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </aside>
        <section className="cqc-card">
          <h3>
            {row.site.name} — {row.element.label}
          </h3>
          <p>
            <strong>Evidence plan: {row.scope.mode.replace(/-/g, " ")}</strong>
          </p>
          <p>{row.scope.reason}</p>
          {profileReady(row.site) && !readOnly && (
            <>
              <label>
                Item relevance{" "}
                <select
                  disabled={busy || legacy?.applicable === false}
                  value={row.scope.mode}
                  onChange={(e) => setScope(row, e.target.value)}
                >
                  <option value="required">Required</option>
                  <option value="supporting">Supporting</option>
                  <option value="not-applicable">Not applicable</option>
                </select>
              </label>
              {legacy?.applicable === false && (
                <p>
                  <button
                    className="cqc-button"
                    disabled={busy}
                    onClick={() =>
                      mutate(() =>
                        cqcApi.setApplicability({
                          siteId: row.site._id,
                          groupId: row.group.id,
                          itemId: row.item.id,
                          applicable: true,
                          reason: "Restored after service-profile review",
                        }),
                      )
                    }
                  >
                    Restore legacy statement exclusion
                  </button>
                </p>
              )}
            </>
          )}
          <p>Source: {row.source?.source || "Not configured"}</p>
          <p>{row.source?.statement}</p>
          <p>{row.source?.documentRef?.label}</p>
          {row.source?.documentRef?.url && (
            <p>
              <a
                target="_blank"
                rel="noreferrer"
                href={row.source.documentRef.url}
              >
                View source
              </a>
            </p>
          )}
          {row.source?.documentRef?.documentId && (
            <p>
              <a
                target="_blank"
                rel="noreferrer"
                href={cqcApi.documentUrl(row.source.documentRef.documentId)}
              >
                View uploaded source
              </a>
            </p>
          )}
          {row.evidenceItems.map((e) => (
            <p key={e._id}>
              <a
                target="_blank"
                rel="noreferrer"
                href={e.documentId ? cqcApi.documentUrl(e.documentId) : e.url}
              >
                {e.label || e.filename || "View evidence"}
              </a>
            </p>
          ))}
          {canEdit && (
            <>
              <p>
                <button
                  className="cqc-button"
                  disabled={busy}
                  onClick={() => setDialog(row)}
                >
                  Configure source / email contributor
                </button>
              </p>
              <label>
                Completion{" "}
                <select
                  disabled={busy}
                  value={row.status}
                  onChange={(e) => {
                    const status = e.target.value;
                    mutate(() =>
                      cqcApi.saveAssignment({
                        siteId: row.site._id,
                        groupId: row.group.id,
                        itemId: row.item.id,
                        elementId: row.element.id,
                        status,
                      }),
                    );
                  }}
                >
                  <option value="not-started">Not started</option>
                  <option value="ongoing">In progress</option>
                  <option value="completed">Completed</option>
                </select>
              </label>
            </>
          )}
          <p>
            <StatusBadge status={row.status} />
          </p>
          <p className="cqc-muted">
            Source configuration and evidence review are separate. Supporting
            items do not reduce readiness. Requests are emailed.
          </p>
          {g.resources.map((r) => (
            <p key={r.url}>
              <a target="_blank" rel="noreferrer" href={r.url}>
                {r.label}
              </a>
            </p>
          ))}
        </section>
      </div>
    );
  }
  if (!data)
    return (
      <div className="cqc-workspace">
        {error ? (
          <p role="alert">
            {error} <button onClick={load}>Retry</button>
          </p>
        ) : (
          "Loading CQC workspace…"
        )}
      </div>
    );
  if (
    !readOnly &&
    sites.length === 1 &&
    (editingAddress || !addressReady(sites[0]))
  )
    return (
      <CqcAddressForm
        site={sites[0]}
        onCancel={
          addressReady(sites[0]) ? () => setEditingAddress(false) : undefined
        }
        onSaved={async () => {
          setEditingAddress(false);
          await load();
        }}
      />
    );
  if (editingProfile && !readOnly)
    return (
      <CqcServiceProfile
        site={editingProfile}
        onCancel={() => setEditingProfile(null)}
        onSaved={async () => {
          setEditingProfile(null);
          await load();
        }}
      />
    );
  return (
    <div className="cqc-workspace" ref={root}>
      <h2>
        {readOnly
          ? "Care compliance inspector evidence pack"
          : sites.length === 1
            ? sites[0].name
            : "Care compliance combined workspace"}
      </h2>
      {error && (
        <p role="alert" className="cqc-error">
          {error}
        </p>
      )}
      <div className="cqc-toolbar">
        {(readOnly ? ["overview"] : ["overview", "setup", "worksheet"]).map(
          (m) => (
            <button
              key={m}
              className={`cqc-button ${mode === m ? "active" : ""}`}
              onClick={() => {
                if (mode === "worksheet") load();
                setMode(m);
                setStep(0);
              }}
            >
              {m === "setup"
                ? "Setup assistant"
                : m === "worksheet"
                  ? "Worksheet"
                  : "Overview"}
            </button>
          ),
        )}
        {!lockSite && allSites.length > 1 && (
          <select
            aria-label="Organisation"
            value={siteId}
            onChange={(e) => {
              setSiteId(e.target.value);
              setGroupFilter("");
              setStep(0);
            }}
          >
            <option value="">All selected organisations</option>
            {allSites.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        {!readOnly && sites.length === 1 && (
          <button
            className="cqc-button"
            onClick={() => setEditingAddress(true)}
          >
            Edit address / country
          </button>
        )}
        {!readOnly && sites.length === 1 && (
          <button
            className="cqc-button"
            onClick={() => configureProfile(sites[0])}
          >
            {profileReady(sites[0])
              ? "Edit service profile"
              : "Create service profile"}
          </button>
        )}
      </div>
      {sites
        .filter((s) => !profileReady(s))
        .map((s) => (
          <div className="cqc-warning" key={s._id}>
            {s.name}:{" "}
            {addressReady(s)
              ? "service profile needs confirmation"
              : "address and country need confirmation"}
            .{" "}
            {!readOnly && (
              <button
                className="cqc-button"
                onClick={() => configureProfile(s)}
              >
                Configure services and relevant items
              </button>
            )}
          </div>
        ))}
      {sites.map((s) => (
        <p className="cqc-muted" key={s._id}>
          {s.name} · {countryOf(s) || "Country unresolved"} ·{" "}
          {frameworkLabel(s)}
        </p>
      ))}
      {sites.length === 1 && sites[0].address && (
        <details className="cqc-card">
          <summary>Service address and jurisdiction</summary>
          <p>
            {[
              sites[0].address.line1,
              sites[0].address.line2,
              sites[0].address.town,
              sites[0].address.county,
              sites[0].address.postcode,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
          <p>Country: {sites[0].address.country || "Unresolved"}</p>
          <p className="cqc-muted">{sites[0].address.countryMethod}</p>
          {sites[0].address.countryOverrideReason && (
            <p>
              Country confirmation / override:{" "}
              {sites[0].address.countryOverrideReason}
            </p>
          )}
          <p className="cqc-muted">
            Confirmed by {sites[0].address.confirmedBy || "Management"}
            {sites[0].address.confirmedAt
              ? ` on ${new Date(sites[0].address.confirmedAt).toLocaleDateString("en-GB")}`
              : " — not confirmed"}
          </p>
        </details>
      )}
      {sites.length === 1 && profileReady(sites[0]) && (
        <details className="cqc-card">
          <summary>
            Service profile and review history · revision{" "}
            {sites[0].profileRevision || 1}
          </summary>
          <p>
            {
              serviceGuidance(sites[0], "safe", "learning-culture", "processes")
                .serviceLabel
            }
          </p>
          <p>
            Confirmed by {sites[0].serviceProfile.confirmedBy || "Management"} ·
            Review due {sites[0].serviceProfile.reviewDate}
          </p>
          <p>{sites[0].serviceProfile.notes}</p>
          <p className="cqc-muted">
            Functions:{" "}
            {serviceGuidance(
              sites[0],
              "safe",
              "learning-culture",
              "processes",
            ).activeFunctions.join(", ") || "None declared / review pending"}
          </p>
          {(sites[0].serviceProfileHistory || [])
            .slice()
            .reverse()
            .map((h) => (
              <p key={h.revision} className="cqc-muted">
                Revision {h.revision} · {h.recordedBy} ·{" "}
                {new Date(h.recordedAt).toLocaleString("en-GB")} ·{" "}
                {h.changes?.decisions
                  ? Object.keys(h.changes.decisions).length
                  : 0}{" "}
                evidence decisions changed
              </p>
            ))}
        </details>
      )}
      {!!retained.length && (
        <details className="cqc-card">
          <summary>
            Retained evidence outside the active framework ({retained.length})
          </summary>
          <p className="cqc-muted">
            Previous evidence remains available for inspection and manual
            review. It is not automatically counted against a different national
            framework.
          </p>
          {retained.map((a, n) => {
            const ref = a.documentRef || a,
              url = ref.documentId
                ? cqcApi.documentUrl(ref.documentId)
                : ref.url;
            return (
              <p key={`${a.retainedKind}-${a._id || n}`}>
                <strong>{a.retainedKind}</strong> · {a.groupId} / {a.itemId} ·{" "}
                {a.source || a.label || a.filename || a.elementId}
                {url && (
                  <>
                    {" "}
                    ·{" "}
                    <a href={url} target="_blank" rel="noreferrer">
                      View retained evidence
                    </a>
                  </>
                )}
                {a.statement && <> — {a.statement}</>}
              </p>
            );
          })}
        </details>
      )}
      {mode === "worksheet" ? (
        <CqcWorklist
          key={siteId || "all"}
          initialSiteId={siteId}
          lockSite={lockSite}
        />
      ) : (
        <>
          <CqcProgress rows={rows} sites={sites} />
          <div className="cqc-toolbar">
            <label>
              Key question{" "}
              <select
                value={groupFilter}
                onChange={(e) => {
                  setGroupFilter(e.target.value);
                  setStep(0);
                }}
              >
                <option value="">All key questions</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
            {mode === "overview" ? (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={showOther}
                    onChange={(e) => setShowOther(e.target.checked)}
                  />{" "}
                  Show supporting, excluded and unprofiled items
                </label>
                <button
                  className="cqc-button"
                  onClick={() =>
                    root.current.querySelectorAll("details").forEach((d) => {
                      d.open = true;
                    })
                  }
                >
                  Expand all
                </button>
                <button
                  className="cqc-button"
                  onClick={() =>
                    root.current.querySelectorAll("details").forEach((d) => {
                      d.open = false;
                    })
                  }
                >
                  Collapse all
                </button>
              </>
            ) : (
              <label>
                <input
                  type="checkbox"
                  checked={onlyUnconfigured}
                  onChange={(e) => {
                    setOnlyUnconfigured(e.target.checked);
                    setStep(0);
                  }}
                />{" "}
                Only items needing a source
              </label>
            )}
          </div>
          {mode === "setup" ? (
            current ? (
              <>
                <p>
                  Item {stepIndex + 1} of {setupRows.length} ·{" "}
                  {current.group.label} › {current.item.label}
                </p>
                {detail(current)}
                <div className="cqc-toolbar">
                  <button
                    className="cqc-button"
                    disabled={stepIndex === 0}
                    onClick={() => setStep(stepIndex - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="cqc-button"
                    disabled={stepIndex >= setupRows.length - 1}
                    onClick={() => setStep(stepIndex + 1)}
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <p>
                No required items match this selection.{" "}
                {stats.pending
                  ? "Confirm a service profile first."
                  : "Review the plan or change the filter."}
              </p>
            )
          ) : (
            groups
              .filter((g) => !groupFilter || g.id === groupFilter)
              .map((group) => {
                const visible = rows.filter(
                    (r) =>
                      r.group.id === group.id &&
                      (showOther || r.scope.mode === "required"),
                  ),
                  summary = readiness(
                    rows.filter((r) => r.group.id === group.id),
                  );
                return (
                  <details className="cqc-card" key={group.id}>
                    <summary>
                      <strong>{group.label}</strong> · {summary.completed}/
                      {summary.total} required items reviewed
                    </summary>
                    {group.items.map((item) => {
                      const rr = visible.filter((r) => r.item.id === item.id);
                      return rr.length ? (
                        <details key={item.id}>
                          <summary>{item.label}</summary>
                          {rr.map((row) => (
                            <details className="cqc-card" key={row.key}>
                              <summary>
                                {row.site.name} · {row.element.label} ·{" "}
                                {row.scope.mode.replace(/-/g, " ")} ·{" "}
                                {row.status === "completed"
                                  ? "Reviewed"
                                  : "Outstanding"}
                              </summary>
                              {detail(row)}
                            </details>
                          ))}
                        </details>
                      ) : null;
                    })}
                    {!visible.length && (
                      <p className="cqc-muted">
                        No required items in this selection. Supporting and
                        excluded evidence remains available using the filter
                        above.
                      </p>
                    )}
                  </details>
                );
              })
          )}
        </>
      )}
      {dialog && !readOnly && (
        <CqcEvidenceDialog
          key={dialog.key}
          site={dialog.site}
          groupId={dialog.group.id}
          itemId={dialog.item.id}
          element={dialog.element}
          existing={dialog.source}
          onClose={() => setDialog(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
